// src/app/modules/Devices/components/DiagnosticsTab.jsx
// Raw request/response log, for installers and firmware developers.
// Only recorded while "Record traffic" is on (kept 14 days; tokens are never stored).

import React, { useRef, useState } from 'react';
import { Terminal, Pause, Play, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import { devicesApi, errorMessage } from '../api';
import { usePolling, useEvent, shortTime } from '../utils';
import { Pill, EmptyState, Button } from './ui';

const POLL_MS = 2000;
const MAX_ROWS = 300;

const statusTone = (s) => (s < 300 ? 'success' : s < 500 ? 'warning' : 'danger');
const pretty = (v) => (v === null || v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v, null, 2));

export function DiagnosticsTab({ orgId, device, canManage, onSaved }) {
  const recording = !!device.settings?.debug_logging;
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(() => new Set());
  const [paused, setPaused] = useState(false);
  const [live, setLive] = useState(false);
  const [toggling, setToggling] = useState(false);
  const since = useRef(null);
  const seen = useRef(new Set());
  const first = useRef(true);

  const poll = useEvent(async () => {
    try {
      const res = await devicesApi.traffic(orgId, device.device_id, { limit: 100, ...(since.current && { since: since.current }) });
      setLive(true);
      const fresh = res.traffic.filter((t) => !seen.current.has(t.log_id));
      if (!fresh.length) { first.current = false; return; }
      since.current = res.traffic[0].at;
      fresh.forEach((t) => seen.current.add(t.log_id));
      const isNew = !first.current;
      first.current = false;
      setRows((prev) => [...fresh.map((t) => ({ ...t, isNew })), ...prev].slice(0, MAX_ROWS));
    } catch (_) {
      setLive(false);
    }
  });
  usePolling(poll, POLL_MS, !paused);

  const toggleRecording = async () => {
    setToggling(true);
    try {
      await devicesApi.update(orgId, device.device_id, { settings: { debug_logging: !recording } });
      toast.success(recording ? 'Traffic recording stopped' : 'Recording every request from this device');
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setToggling(false);
    }
  };

  const toggleRow = (id) => setOpen((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <>
      <div className="dv-card__head" style={{ borderBottom: rows.length ? undefined : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className={`dv-live-dot${live && !paused && recording ? ' on' : ''}`} />
          <div>
            <h2>Live traffic</h2>
            <p>{recording ? 'Every request this device sends, and what Sety answered.' : 'Recording is off.'}</p>
          </div>
        </div>
        <div className="dv-toolbar">
          <Button size="sm" variant="ghost" icon={paused ? Play : Pause} onClick={() => setPaused((p) => !p)}>{paused ? 'Resume' : 'Pause'}</Button>
          {rows.length > 0 && <Button size="sm" variant="ghost" onClick={() => setRows([])}>Clear</Button>}
          {canManage && (
            <Button size="sm" variant={recording ? 'danger' : 'primary'} loading={toggling} onClick={toggleRecording}>
              {recording ? 'Stop recording' : 'Start recording'}
            </Button>
          )}
        </div>
      </div>

      {!recording && rows.length === 0 && (
        <EmptyState icon={Terminal} title="Traffic recording is off">
          Turn it on while installing or debugging a device to see each request and response.
          Records are kept for 14 days and never include the device’s secret token.
        </EmptyState>
      )}

      {recording && rows.length === 0 && (
        <div className="dv-card__body">
          <div className="dv-notice dv-notice--brand">
            <Info size={16} />
            <span>Waiting for the device… It only connects when someone presses its button or scans a finger.</span>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="dv-traffic">
          {rows.map((t) => (
            <div key={t.log_id} className={`dv-tx${t.isNew ? ' new' : ''}`}>
              <button type="button" className="dv-tx__sum" onClick={() => toggleRow(t.log_id)} aria-expanded={open.has(t.log_id)}>
                <span className="dv-tx__time">{shortTime(t.at)}</span>
                <span className="dv-tx__path"><span className="dv-tx__method">{t.method}</span>{t.path}</span>
                <Pill tone={statusTone(t.status)}>{t.status}</Pill>
                <span className="dv-tx__ms">{t.duration_ms} ms</span>
              </button>
              {open.has(t.log_id) && (
                <div className="dv-tx__body">
                  <div>
                    <h4>Request{t.auth_result && t.auth_result !== 'ok' ? ` · auth: ${t.auth_result.replace(/_/g, ' ')}` : ''}</h4>
                    <pre>{pretty(t.request_body || t.query)}</pre>
                  </div>
                  <div>
                    <h4>Response</h4>
                    <pre>{pretty(t.response_body)}</pre>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default DiagnosticsTab;
